import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import PauseOutlinedIcon from "@mui/icons-material/PauseOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import RestaurantOutlinedIcon from "@mui/icons-material/RestaurantOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import {
  fetchClockEntries,
  fetchClockStatus,
  fetchVacationBalances,
  postClock,
} from "../../../api/TimeTracking/timeTrackingApi";
import {
  formatGeolocationHint,
  getClockGeolocation,
  isGeolocationSecureContext,
  isMobileBrowser,
} from "../../../utils/clockGeolocation";
import { pageFieldSx } from "../../../styles/pageModernStyles";
import {
  ttClockPauseBtnSx,
  ttClockPunchButtonSx,
  ttClockStatusRingSx,
  ttDesktopSidePanelSx,
} from "./timeTrackingStyles";
import TabContentShell from "./TabContentShell";
import { eventLabel, PAUSE_TYPE_OPTIONS } from "./clockEventLabels";
import { VacationBalanceCompact } from "./VacationBalancePanel";
import {
  computeTodayWorkedSeconds,
  formatElapsedSeconds,
  localDateString,
} from "./dailyBoardUtils";

const WORK_MODES = [
  { value: "presencial", label: "Presencial" },
  { value: "teletrabajo", label: "Teletrabajo" },
  { value: "hibrido", label: "Híbrido" },
];

function formatDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-ES", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

const ClockTab = ({ selectedUserId, currentUserId, canRrhh, canFichaje }) => {
  const theme = useTheme();
  const year = new Date().getFullYear();
  const [workMode, setWorkMode] = useState("presencial");
  const [status, setStatus] = useState(null);
  const [vacationBalance, setVacationBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [error, setError] = useState(null);
  const [geoHint, setGeoHint] = useState(null);
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [todayEntries, setTodayEntries] = useState([]);
  const [now, setNow] = useState(() => new Date());
  const mobile = isMobileBrowser();

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const clockUserId = selectedUserId || currentUserId;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const statusParams = clockUserId ? { userId: clockUserId } : {};
      const balanceUserId = clockUserId;
      const viewingOther = canRrhh && selectedUserId;
      const showBalance =
        balanceUserId && ((canFichaje && !viewingOther) || viewingOther);

      const balanceParams = {
        year,
        userId: balanceUserId,
      };

      const today = localDateString();
      const entriesParams = { from: today, to: today, limit: 200 };
      if (clockUserId) entriesParams.userId = clockUserId;

      const [statusData, balanceData, entriesData] = await Promise.all([
        fetchClockStatus(statusParams),
        showBalance
          ? fetchVacationBalances(balanceParams).catch(() => null)
          : Promise.resolve(null),
        clockUserId || !canRrhh
          ? fetchClockEntries(entriesParams).catch(() => [])
          : Promise.resolve([]),
      ]);

      setStatus(statusData);
      setTodayEntries(Array.isArray(entriesData) ? entriesData : []);
      const balanceRow =
        balanceData?.employees?.find(
          (e) => Number(e.userId) === Number(balanceUserId),
        ) || balanceData?.employees?.[0];
      setVacationBalance(balanceRow || null);
    } catch (e) {
      setError(
        e?.response?.data?.error || "No se pudo cargar el estado de fichaje",
      );
    } finally {
      setLoading(false);
    }
  }, [clockUserId, selectedUserId, canFichaje, canRrhh, year]);

  useEffect(() => {
    load();
  }, [load]);

  const handleClock = async (eventType, pauseType) => {
    try {
      setActing(true);
      setError(null);
      setGeoHint(null);
      setPauseDialogOpen(false);

      setGeoHint("Obteniendo ubicación…");
      const geo = await getClockGeolocation();
      setGeoHint(formatGeolocationHint(geo));

      await postClock({
        eventType,
        workMode,
        pauseType,
        clientSource: mobile ? "mobile" : "web",
        geoSource: geo.geoSource,
        latitude: geo.latitude,
        longitude: geo.longitude,
        accuracyMeters: geo.accuracyMeters,
      });
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || "Error al fichar");
    } finally {
      setActing(false);
    }
  };

  const liveTime = useMemo(
    () =>
      now.toLocaleTimeString("es-ES", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }),
    [now],
  );

  const liveDate = useMemo(
    () =>
      now.toLocaleDateString("es-ES", {
        weekday: "long",
        day: "numeric",
        month: "long",
      }),
    [now],
  );

  const isClockedIn = status?.isClockedIn;
  const isOnPause = status?.isOnPause;

  const clockAccent = !isClockedIn
    ? theme.palette.error.main
    : isOnPause
      ? theme.palette.warning.main
      : theme.palette.success.main;

  const todaySeconds = useMemo(
    () => computeTodayWorkedSeconds(todayEntries, now, status, clockUserId),
    [todayEntries, now, status, clockUserId],
  );

  const showTodayWorked = isClockedIn || todaySeconds > 0;

  if (loading) {
    return (
      <TabContentShell centered accentColor={theme.palette.primary.main}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      </TabContentShell>
    );
  }

  const viewingOther = canRrhh && selectedUserId;
  const canClockSelf = canFichaje && !viewingOther;
  const showVacationBalance =
    vacationBalance && ((canFichaje && !viewingOther) || viewingOther);

  const punchAccent = clockAccent;
  const statusColor = clockAccent;

  const StatusIcon = !isClockedIn
    ? LoginOutlinedIcon
    : isOnPause
      ? PlayArrowOutlinedIcon
      : LogoutOutlinedIcon;

  const punchLabel = !isClockedIn
    ? "ENTRAR"
    : isOnPause
      ? "Fin pausa"
      : "SALIR";

  const handlePunchClick = () => {
    if (!isClockedIn) handleClock("clock_in");
    else if (isOnPause) handleClock("pause_end");
    else handleClock("clock_out");
  };

  return (
    <Box>
      {error ? (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      ) : null}

      {canFichaje && !canRrhh ? (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          ¿Hora incorrecta u olvidaste fichar? Solicita una corrección en
          Historial o en la pestaña Solicitudes. RRHH debe aprobarla antes de
          que se aplique.
        </Alert>
      ) : null}

      <TabContentShell accentColor={canClockSelf ? punchAccent : statusColor}>
        <Grid container spacing={{ xs: 2.5, md: 3 }} alignItems="stretch">
          <Grid item xs={12} md={5} lg={4}>
            <Stack
              spacing={2.5}
              alignItems="center"
              sx={{ width: "100%", ...ttDesktopSidePanelSx(theme) }}
            >
              {canClockSelf && !isClockedIn ? (
                <TextField
                  select
                  size="small"
                  label="Modalidad"
                  value={workMode}
                  onChange={(e) => setWorkMode(e.target.value)}
                  sx={{
                    ...pageFieldSx(theme),
                    width: "100%",
                    maxWidth: 320,
                  }}
                >
                  {WORK_MODES.map((m) => (
                    <MenuItem key={m.value} value={m.value}>
                      {m.label}
                    </MenuItem>
                  ))}
                </TextField>
              ) : null}

              {canClockSelf ? (
                <ButtonBase
                  disabled={acting}
                  onClick={handlePunchClick}
                  aria-label={punchLabel}
                  sx={ttClockPunchButtonSx(theme, {
                    accentColor: punchAccent,
                    pulsing: !isClockedIn,
                  })}
                >
                  <StatusIcon
                    sx={{
                      fontSize: { xs: 30, sm: 34 },
                      color: punchAccent,
                      mb: 0.35,
                    }}
                  />
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      fontVariantNumeric: "tabular-nums",
                      letterSpacing: 0.5,
                      lineHeight: 1,
                      fontSize: { xs: "1.45rem", sm: "1.6rem" },
                      color: punchAccent,
                    }}
                  >
                    {liveTime}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      mt: 0.35,
                      textAlign: "center",
                      px: 1,
                      fontWeight: 600,
                      textTransform: "capitalize",
                      lineHeight: 1.2,
                      fontSize: "0.65rem",
                    }}
                  >
                    {liveDate}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      mt: 0.75,
                      fontWeight: 800,
                      fontSize: "0.72rem",
                      letterSpacing: 0.6,
                      textTransform: "uppercase",
                      color: punchAccent,
                    }}
                  >
                    {punchLabel}
                  </Typography>
                </ButtonBase>
              ) : (
                <Box
                  sx={ttClockStatusRingSx(theme, {
                    statusColor,
                    pulsing: false,
                  })}
                >
                  <AccessTimeOutlinedIcon
                    sx={{
                      fontSize: { xs: 28, sm: 32 },
                      color: statusColor,
                      mb: 0.5,
                      opacity: isClockedIn ? 1 : 0.65,
                    }}
                  />
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      fontVariantNumeric: "tabular-nums",
                      letterSpacing: 0.5,
                      lineHeight: 1,
                      fontSize: { xs: "1.35rem", sm: "1.5rem" },
                      color: statusColor,
                    }}
                  >
                    {liveTime}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      mt: 0.5,
                      textAlign: "center",
                      px: 1,
                      fontWeight: 600,
                      textTransform: "capitalize",
                      lineHeight: 1.2,
                      fontSize: "0.65rem",
                    }}
                  >
                    {liveDate}
                  </Typography>
                </Box>
              )}

              {canClockSelf && isClockedIn && !isOnPause ? (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PauseOutlinedIcon />}
                  disabled={acting}
                  onClick={() => setPauseDialogOpen(true)}
                  sx={ttClockPauseBtnSx(theme)}
                >
                  PAUSAR
                </Button>
              ) : null}

              {showTodayWorked ? (
                <Chip
                  label={
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        lineHeight: 2.2,
                        color: "inherit",
                        opacity: 0.82,
                      }}
                    >
                      Trabajado hoy:{" "}
                      <Box
                        component="span"
                        sx={{
                          fontWeight: 800,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {formatElapsedSeconds(todaySeconds)}
                      </Box>
                    </Typography>
                  }
                  sx={{
                    height: "auto",
                    minHeight: 32,
                    py: 0.5,
                    px: 1.25,
                    fontWeight: 800,
                    fontSize: "0.85rem",
                    fontVariantNumeric: "tabular-nums",
                    bgcolor: alpha(statusColor, 0.14),
                    color: !isClockedIn
                      ? theme.palette.error.dark
                      : isOnPause
                        ? theme.palette.warning.dark
                        : theme.palette.success.dark,
                    border: "1px solid",
                    borderColor: alpha(statusColor, 0.35),
                  }}
                />
              ) : null}

              {geoHint ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                >
                  {geoHint}
                </Typography>
              ) : null}
            </Stack>
          </Grid>

          <Grid item xs={12} md={7} lg={8}>
            <Stack
              spacing={2.5}
              sx={{
                width: "100%",
                height: "100%",
                ...ttDesktopSidePanelSx(theme),
              }}
            >
              {status?.lastEntry ? (
                <Box
                  sx={{
                    width: "100%",
                    textAlign: { xs: "center", md: "left" },
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.text.primary, 0.03),
                    border: "1px solid",
                    borderColor: alpha(theme.palette.divider, 0.65),
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontWeight: 700,
                      letterSpacing: 0.5,
                      textTransform: "uppercase",
                    }}
                  >
                    Último registro
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 700, mt: 0.25 }}
                  >
                    {eventLabel(
                      status.lastEntry.eventType,
                      status.lastEntry.pauseType,
                    )}
                    {" · "}
                    {formatDateTime(status.lastEntry.recordedAt)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {status.lastEntry.workMode}
                    {status.lastEntry.geoSource === "browser" &&
                    status.lastEntry.latitude != null
                      ? ` · GPS ±${Math.round(status.lastEntry.accuracyMeters || 0)} m`
                      : ""}
                  </Typography>
                </Box>
              ) : null}

              {showVacationBalance ? (
                <VacationBalanceCompact balance={vacationBalance} year={year} />
              ) : null}

              {canClockSelf && !isGeolocationSecureContext() ? (
                <Alert
                  severity="warning"
                  sx={{ width: "100%", borderRadius: 2 }}
                >
                  Estás en HTTP. El navegador no dará ubicación; usa HTTPS para
                  registrar GPS.
                </Alert>
              ) : null}

              {canClockSelf && isGeolocationSecureContext() ? (
                <Stack
                  direction="row"
                  spacing={0.75}
                  alignItems="flex-start"
                  sx={{
                    width: "100%",
                    p: 1.25,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.info.main, 0.06),
                    border: `1px solid ${alpha(theme.palette.info.main, 0.16)}`,
                  }}
                >
                  <LocationOnOutlinedIcon
                    sx={{ fontSize: 18, color: "info.main", mt: 0.15 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    En cada fichaje se pide ubicación al navegador. Si no
                    aparece GPS en historial, revisa el permiso de ubicación del
                    sitio.
                  </Typography>
                </Stack>
              ) : null}

              {!canClockSelf ? (
                <Typography variant="body2" color="text.secondary">
                  {viewingOther
                    ? "Vista de consulta del empleado seleccionado."
                    : "Sin permiso de fichaje (solo consulta RRHH)."}
                </Typography>
              ) : null}
            </Stack>
          </Grid>
        </Grid>
      </TabContentShell>

      <Dialog
        open={pauseDialogOpen}
        onClose={() => !acting && setPauseDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: theme.palette.primary.dark,
            color: "common.white",
            backgroundImage: "none",
          },
        }}
      >
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Stack spacing={2} alignItems="center">
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                bgcolor: theme.palette.warning.main,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PauseOutlinedIcon sx={{ fontSize: 28, color: "common.white" }} />
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                PAUSAR
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.25 }}>
                Elige un tipo de pausa
              </Typography>
            </Box>
            <List
              disablePadding
              sx={{
                width: "100%",
                border: "1px solid",
                borderColor: alpha("#fff", 0.2),
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              {PAUSE_TYPE_OPTIONS.map((opt, idx) => (
                <React.Fragment key={opt.value}>
                  {idx > 0 ? (
                    <Divider sx={{ borderColor: alpha("#fff", 0.15) }} />
                  ) : null}
                  <ListItemButton
                    disabled={acting}
                    onClick={() => handleClock("pause_start", opt.value)}
                    sx={{
                      py: 1.5,
                      "&:hover": { bgcolor: alpha("#fff", 0.08) },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: "common.white" }}>
                      <RestaurantOutlinedIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={opt.label}
                      primaryTypographyProps={{
                        fontWeight: 700,
                        textTransform:
                          opt.value === "comida" ? "uppercase" : "none",
                      }}
                    />
                  </ListItemButton>
                </React.Fragment>
              ))}
            </List>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ClockTab;
